// mapWithConcurrency — map over items with at most N in flight at once.
//
// Exists because a `for (const x of xs) await f(x)` loop and a bare
// `Promise.all(xs.map(f))` are both wrong when `f` makes a request, and the
// failure modes are opposite:
//
//   • Serial (`await` in a loop) is what the HDRI switcher did across every
//     environment row. Each probe holds one of the browser's 6 connections to
//     the origin for its whole round trip, and the NEXT one only starts when it
//     finishes — so on a 59-environment org the sweep is 59 round trips long and
//     runs for as long as the page is congested. Measured 2026-08-26: one new
//     probe every ~25s, indefinitely, while an in-page fetch of a trivial static
//     file took 50s (5ms over shell curl) because the socket pool was saturated.
//   • Promise.all fires all 59 at once, which saturates that pool itself and
//     starves whatever the app is actually loading.
//
// Bounded is the only shape that is neither. Results come back in INPUT order
// regardless of completion order, so callers can zip them against their input.
//
// A rejection from `fn` rejects the whole call (standard Promise.all semantics)
// and remaining items are not started. Callers that want per-item tolerance
// catch inside `fn` — which is also what makes "this probe 404'd" ordinary data
// rather than an error, per the loud-failure rule in CLAUDE.md: a 404 here is a
// real answer ("not baked"), not a failure to report.

/**
 * @template T, R
 * @param {Array<T>} items
 * @param {number} limit  max concurrent calls; clamped to >= 1.
 * @param {(item: T, index: number) => Promise<R>|R} fn
 * @returns {Promise<Array<R>>} results in input order.
 */
export async function mapWithConcurrency(items, limit, fn) {
  const list = Array.isArray(items) ? items : []
  if (list.length === 0) return []
  // A non-positive / non-finite limit would either spin forever or fire
  // everything at once — the two things this function exists to prevent. Clamp
  // rather than throw: a caller computing a limit from a config value should
  // degrade to serial, not crash the page.
  const width = Math.max(1, Math.min(Math.floor(Number(limit)) || 1, list.length))
  const results = new Array(list.length)
  let next = 0

  // `width` workers pulling from a shared cursor, rather than fixed-size
  // batches: a batch is only as fast as its slowest member, so one slow probe
  // in a batch of 4 idles the other three slots until it lands.
  const worker = async () => {
    while (true) {
      const i = next++
      if (i >= list.length) return
      results[i] = await fn(list[i], i)
    }
  }

  await Promise.all(Array.from({ length: width }, worker))
  return results
}
